/**
 * How a user's roles are named in the UI.
 *
 * The identity chip used to render `role[0]`, and that ordering comes from
 * `UserManager.GetRolesAsync` — i.e. arbitrary database order. An account holding both
 * `Party Admin` and `Super Admin` therefore displayed "Party Admin" while wielding
 * Super Admin's entire grant set, which is exactly how a screen full of system modules
 * gets mistaken for a Party Admin permissions bug.
 *
 * Permissions themselves stay a union across all roles — that matches the backend,
 * where `TokenService` unions at mint time and `HasActionHandler` succeeds on any one
 * matching claim. Only the *label* is a single value, so it must be the one that
 * explains the most access rather than whichever row the database returned first.
 */

/** Most privileged first. Anything unlisted sorts last, in its original order. */
const PRECEDENCE = [
	'super admin',
	'administrator',
	'party admin',
	'observer admin',
	'candidate',
	'result analyst',
	'party agent',
];

const rank = (role: string): number => {
	const key = role.trim().toLowerCase();
	const index = PRECEDENCE.findIndex((entry) => key === entry || key.startsWith(`${entry} `));
	return index === -1 ? PRECEDENCE.length : index;
};

/** The role to show, chosen by privilege rather than by array position. */
export const primaryRole = (roles: readonly string[]): string => {
	if (roles.length === 0) return 'Member';
	return [...roles].sort((a, b) => rank(a) - rank(b))[0];
};

/** Full, honest list for the accessible name — never hide roles from assistive tech. */
export const rolesLabel = (roles: readonly string[]): string => {
	if (roles.length === 0) return 'No roles assigned';
	if (roles.length === 1) return roles[0];
	return `${roles.length} roles: ${roles.join(', ')}`;
};

/** Suffix showing how many roles are not named in the chip, e.g. '+1'. */
export const extraRoleCount = (roles: readonly string[]): number => Math.max(0, roles.length - 1);
